import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { employeeSchema, type EmployeeFormData } from '@/lib/validators';
import { useEmployee, useEmployees, useNextEmployeeCode } from '@/composables/payroll/useEmployees';
import { useOrganizationSettings } from '@/composables/shared/useOrganizationSettings';
import { useUsers } from '@/composables/shared/useUsers';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormDatePicker } from '@/components/forms/FormDatePicker';
import { EMPLOYEE_STATUS_OPTIONS, EMPLOYMENT_TYPE_OPTIONS } from '@/types/payroll';

/** Sentinel for "no linked login" — Radix Select cannot hold an empty value. */
const NO_LOGIN = '__none__';

/** Strips '' → undefined so blank optional fields are omitted, not sent empty. */
function clean(data: EmployeeFormData): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(data).filter(([, v]) => v !== '' && v !== undefined && v !== null && v !== NO_LOGIN),
  );
}

export function EmployeeCreatePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const { employee } = useEmployee(id ?? '');
  const { createEmployee, updateEmployee } = useEmployees();
  const { nextCode } = useNextEmployeeCode(!isEdit);
  const { settings } = useOrganizationSettings();
  const { users } = useUsers();

  const form = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      employeeCode: '', firstName: '', lastName: '', email: '', phone: '',
      designation: '', department: '', employmentType: 'FULL_TIME', status: 'ACTIVE',
      dateOfJoining: '', dateOfExit: '', dateOfBirth: '',
      taxIdentifier: '', socialSecurityId: '', pensionId: '',
      bankName: '', bankAccountName: '', bankAccountNumber: '', bankIfsc: '',
      userId: NO_LOGIN, notes: '',
    },
  });

  useEffect(() => {
    if (!employee) return;
    form.reset({
      employeeCode: employee.employeeCode ?? '',
      firstName: employee.firstName, lastName: employee.lastName,
      email: employee.email ?? '', phone: employee.phone ?? '',
      designation: employee.designation ?? '', department: employee.department ?? '',
      employmentType: employee.employmentType, status: employee.status,
      dateOfJoining: employee.dateOfJoining?.slice(0, 10) ?? '',
      dateOfExit: employee.dateOfExit?.slice(0, 10) ?? '',
      dateOfBirth: employee.dateOfBirth?.slice(0, 10) ?? '',
      taxIdentifier: employee.taxIdentifier ?? '',
      socialSecurityId: employee.socialSecurityId ?? '',
      pensionId: employee.pensionId ?? '',
      bankName: employee.bankName ?? '', bankAccountName: employee.bankAccountName ?? '',
      bankAccountNumber: employee.bankAccountNumber ?? '', bankIfsc: employee.bankIfsc ?? '',
      userId: employee.userId ?? NO_LOGIN, notes: employee.notes ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.id]);

  const onSubmit = (data: EmployeeFormData) => {
    const payload = clean(data);
    if (isEdit) {
      // Send explicit nulls so clearing a link or an exit date actually clears it —
      // `clean` drops empties, which would otherwise read as "leave unchanged".
      if (!data.userId || data.userId === NO_LOGIN) payload.userId = null;
      if (!data.dateOfExit) payload.dateOfExit = null;
      updateEmployee.mutate({ id: id as string, data: payload }, {
        onSuccess: () => navigate(`/people/employees/${id}`),
      });
    } else {
      createEmployee.mutate(payload, {
        onSuccess: (res) => navigate(`/people/employees/${res.data.id}`),
      });
    }
  };

  const isPending = createEmployee.isPending || updateEmployee.isPending;

  return (
    <PageContainer backTo
      title={isEdit ? 'Edit Employee' : 'New Employee'}
      description={isEdit ? undefined : 'Contractors and interns can be added without a login account.'}
      
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="text-base">Personal</CardTitle></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <FormField form={form} name="firstName" label="First name" required />
              <FormField form={form} name="lastName" label="Last name" required />
              <FormField form={form} name="email" label="Email" type="email" />
              <FormField form={form} name="phone" label="Phone" />
              <FormDatePicker form={form} name="dateOfBirth" label="Date of birth" />
              <FormField
                form={form} name="employeeCode" label="Employee code"
                placeholder={nextCode ?? 'auto'}
                description={isEdit ? 'Changing this affects future documents only.' : `Leave blank to use ${nextCode ?? 'the next code'}.`}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Employment</CardTitle></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <FormField form={form} name="designation" label="Designation" />
              <FormField form={form} name="department" label="Department" />
              <FormSelect form={form} name="employmentType" label="Employment type" options={EMPLOYMENT_TYPE_OPTIONS.map((o) => ({ label: o.label, value: o.value }))} />
              <FormSelect form={form} name="status" label="Status" options={EMPLOYEE_STATUS_OPTIONS.map((o) => ({ label: o.label, value: o.value }))} />
              <FormDatePicker form={form} name="dateOfJoining" label="Date of joining" required />
              <FormDatePicker form={form} name="dateOfExit" label="Date of exit" />
              <div className="sm:col-span-2">
                <FormSelect
                  form={form} name="userId" label="Linked login"
                  // Radix rejects an empty-string option value, so "no login"
                  // travels as a sentinel and is mapped back on submit.
                  options={[
                    { label: 'No login', value: NO_LOGIN },
                    ...users.map((u) => ({ label: `${u.firstName} ${u.lastName} (${u.email})`, value: u.id })),
                  ]}
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  Lets this person sign in and download their own payslips. Optional.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Statutory identifiers</CardTitle></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              {/* Labels are org-configurable, so a non-Indian deployment renames them. */}
              <FormField form={form} name="taxIdentifier" label={settings?.taxIdentifierLabel || 'PAN'} />
              <FormField form={form} name="socialSecurityId" label={settings?.socialSecurityIdLabel || 'ESI Number'} />
              <FormField form={form} name="pensionId" label={settings?.pensionIdLabel || 'UAN'} className="sm:col-span-2" />
              <p className="text-xs text-muted-foreground sm:col-span-2">
                Rename these labels in Settings → Documents → Payroll.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Salary payout</CardTitle></CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <FormField form={form} name="bankName" label="Bank name" />
              <FormField form={form} name="bankAccountName" label="Account holder" />
              <FormField form={form} name="bankAccountNumber" label="Account number" />
              <FormField form={form} name="bankIfsc" label="IFSC / routing code" />
              <FormTextarea form={form} name="notes" label="Notes" className="sm:col-span-2" />
            </CardContent>
          </Card>
        </div>

        <div className="mt-6 flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create employee'}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
